import {
  Button,
  Paper,
  type ButtonProps,
  Dialog,
  type DialogProps,
  Box,
  styled,
  DialogTitle,
  DialogTitleProps,
  useMediaQuery,
} from '@mui/material';
import React, {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useId,
  useMemo,
  useState,
  type CSSProperties,
  JSX,
} from 'react';

import type { Texts } from '../../types';

import { PopUpConfirmBoxButtonWrapper, popUpConfirmBoxStyled } from './styled';

type TextsKeys = 'yes' | 'no' | 'title' | 'extraButton';

const defaultTexts: Partial<Texts<TextsKeys>> = {
  yes: 'Delete',
  no: 'Cancel',
  extraButton: 'Rematch',
  title: 'Confirmation',
};

export type PopUpConfirmBoxProps = PropsWithChildren<{
  open: boolean;
  callback: (status: boolean) => void;
  onClickDeleteBox: () => void;
  texts?: Partial<Texts<TextsKeys>>;
  titleProps?: {
    showTitle: boolean;
    props?: DialogTitleProps;
  };
  outerBoxProps?: Omit<DialogProps, 'open' | 'children'>;
  footerWrapperProps?: CSSProperties & {
    translate?: 'yes' | 'no';
  };
  yesButtonProps?: {
    showYesButton: boolean;
    props?: ButtonProps;
  };
  noButtonProps?: {
    showNoButton: boolean;
    props?: ButtonProps;
  };
  extraButtonProps?: {
    showExtraButton?: boolean;
    props?: ButtonProps;
  };
  isClickAwayListener?: boolean;
}>;

export interface DisplayPopUpConfirmBoxProps {
  message: string | JSX.Element;
  callback: PopUpConfirmBoxProps['callback'];
}

export type AddPopUpBox = (
  content: DisplayPopUpConfirmBoxProps,
  optionalProps?: Omit<
    PopUpConfirmBoxProps,
    'children' | 'callback' | 'message' | 'onClickDeleteBox' | 'open'
  >
) => void;

export type PopUpBoxInitial = {
  addPopUpBox: AddPopUpBox;
  onClick: () => void;
};

const initialValue: PopUpBoxInitial = {
  addPopUpBox: () => {},
  onClick: () => {},
};

const PopUpBox = createContext<PopUpBoxInitial>(initialValue);

export const usePopUpBox = () => useContext(PopUpBox);

export const usePopUpBoxInitializer = () => {
  const [showPopUpBox, setShowPopUpBox] = useState<boolean>(false);
  const [popUpBoxProps, setPopUpBoxProps] =
    useState<DisplayPopUpConfirmBoxProps>({ message: '', callback: () => {} });

  const [popUpConfirmBoxOptionalProps, setPopUpConfirmBoxOptionalProps] =
    useState<
      Omit<
        PopUpConfirmBoxProps,
        'children' | 'callback' | 'message' | 'onClickDeleteBox' | 'open'
      >
    >();

  const addPopUpBox = useCallback<PopUpBoxInitial['addPopUpBox']>(
    (content, optionalProps) => {
      setShowPopUpBox(true);
      setPopUpBoxProps(content);
      setPopUpConfirmBoxOptionalProps(optionalProps);
    },
    [setShowPopUpBox]
  );

  const onClick = () => {
    setShowPopUpBox(prev => !prev);
  };

  const value: PopUpBoxInitial = useMemo(
    () => ({
      addPopUpBox,
      onClick,
    }),
    [addPopUpBox]
  );

  return {
    value,
    showPopUpBox,
    popUpBoxProps,
    onClick,
    addPopUpBox,
    popUpConfirmBoxOptionalProps,
  };
};

export const PopUpBoxProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    value,
    showPopUpBox,
    popUpBoxProps,
    onClick,
    popUpConfirmBoxOptionalProps,
  } = usePopUpBoxInitializer();
  const { message, callback } = popUpBoxProps;

  return (
    <PopUpBox.Provider value={value}>
      <PopUpConfirmBox
        callback={callback}
        onClickDeleteBox={onClick}
        open={showPopUpBox}
        {...popUpConfirmBoxOptionalProps}
      >
        {message}
      </PopUpConfirmBox>
      {children}
    </PopUpBox.Provider>
  );
};

export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Shared confirm dialog (leave table, rematch, profile forms). MUI provides
 * the focus trap, Escape and focus restore; this adds labelled/described
 * semantics, a safe initial focus for destructive confirmations and honours
 * reduced motion.
 */
export const PopUpConfirmBox: FC<PopUpConfirmBoxProps> = ({
  callback,
  onClickDeleteBox,
  children,
  texts = defaultTexts,
  titleProps = {
    showTitle: true,
    props: {},
  },
  outerBoxProps = {
    PaperProps: {
      sx: popUpConfirmBoxStyled,
    },
  },
  footerWrapperProps = {},
  yesButtonProps = {
    showYesButton: true,
    props: {},
  },
  noButtonProps = {
    showNoButton: true,
    props: {},
  },
  extraButtonProps,
  open,
}) => {
  const id = useId();
  const titleId = `${id}-title`;
  const bodyId = `${id}-body`;
  const reduceMotion = useMediaQuery(REDUCED_MOTION_QUERY, { noSsr: true });
  const showTitle = titleProps.showTitle;
  const hasTitleText = showTitle && !!texts.title;
  // For a destructive confirm, land on the safe choice first.
  const focusCancel =
    yesButtonProps.showYesButton && noButtonProps.showNoButton;

  return (
    <CustomDialog
      {...{
        open,
        onClose: onClickDeleteBox,
        PaperComponent: Paper,
        fullWidth: true,
        sx: {
          padding: 3,
        },
        'aria-labelledby': hasTitleText ? titleId : undefined,
        'aria-describedby': bodyId,
        ...(reduceMotion ? { transitionDuration: 0 } : {}),
        ...outerBoxProps,
      }}
    >
      {showTitle && (
        <DialogTitle
          id={titleId}
          variant="h3"
          sx={{ px: 1, fontWeight: 700 }}
          {...titleProps.props}
        >
          {texts.title}
        </DialogTitle>
      )}
      <Box
        id={bodyId}
        px={1}
        fontSize={18}
        sx={{ overflowY: 'auto', width: '100%' }}
      >
        {children}
      </Box>
      <PopUpConfirmBoxButtonWrapper {...footerWrapperProps}>
        {yesButtonProps.showYesButton && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              callback(true);
              onClickDeleteBox();
            }}
            {...yesButtonProps.props}
          >
            {texts.yes}
          </Button>
        )}

        {noButtonProps.showNoButton && (
          <Button
            variant="outlined"
            color="success"
            onClick={onClickDeleteBox}
            autoFocus={focusCancel}
            {...noButtonProps.props}
          >
            {texts.no}
          </Button>
        )}
        {extraButtonProps?.showExtraButton && (
          <Button
            variant="outlined"
            color="primary"
            onClick={extraButtonProps.props?.onClick}
            {...extraButtonProps.props}
          >
            {texts.extraButton}
          </Button>
        )}
      </PopUpConfirmBoxButtonWrapper>
    </CustomDialog>
  );
};

export const formPopUpOptionalProps: Omit<
  PopUpConfirmBoxProps,
  'message' | 'children' | 'callback' | 'open' | 'onClickDeleteBox'
> = {
  yesButtonProps: {
    showYesButton: false,
  },
  texts: {
    title: ``,
    yes: '',
    no: 'Close',
    extraButton: '',
  },
  outerBoxProps: {
    maxWidth: 'lg',
    PaperProps: {
      sx: theme => ({
        paddingTop: theme.spacing(8),
        padding: theme.spacing(5),
        flex: 'flex',
      }),
    },
  },
};

const CustomDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    border: `1px solid ${theme.palette.primary.main}`,
    // Never clip content: long messages scroll inside the dialog.
    maxHeight: 'min(560px, calc(100dvh - 32px))',
    boxShadow: theme.shadows[8],
  },
}));
